// MACHINERY V2 (Stroje) — the catalog browse page's data half (M2.8).
//
// Spec: data package §11 (data sources + states). Reference impl:
// components/CatalogBrowsePage.tsx :267-280 (the three load effects) and
// :423-482 (search debounce + clear).
//
// >>> §7 FIX 5 EXTENDED TO THE CATALOG BROWSE. <<<
// `browseCatalogModels.rejected` sets ONLY `browseLoading = false`
// (machineCatalogSlice.ts:124-126 — there is no error field on the slice at
// all), so in v1 a failed search is indistinguishable from a search that
// legitimately matched nothing: the page renders "No models found" and a Clear
// button that cannot help. Same shape as the Specs-tab failure Wave 4 fixed.
// The failure is tracked HERE, off the dispatch's own promise, exactly as
// SpecificationsTabV2.tsx:66-71 does — the slice shape is NOT changed (ticket
// §2.3: "Do NOT mutate any existing slice's shape").
//
// >>> ONLY THE LATEST REQUEST MAY PAINT — AND ONLY IT MAY RAISE THE ERROR. <<<
// The rail publishes a new query as the user works, so several
// `browseCatalogModels` can be in flight at once and they can resolve out of
// order. The slice writes browseResults / browseTotalCount UNCONDITIONALLY on
// every fulfilled action (machineCatalogSlice.ts:118-123 — no requestId
// bookkeeping), so a superseded response landing last would paint its rows
// under a rail that has moved on; and a superseded REJECTION would flip the
// RetryNotice on over a grid that had just loaded fine. Both are prevented the
// same way: `requestRef` stamps each dispatch and a settled promise is ignored
// unless its stamp is still the current one. The rows are therefore held HERE,
// off the dispatch's own resolved payload, rather than read back from the
// shared slice — that slice is the live surface's too and its shape must not
// change (ticket §2.3). The debounce on the rail's range inputs
// (catalogRailAtoms.tsx `RangeInput`) cuts how many requests overlap in the
// first place; this guard is what makes the survivors safe.
//
// >>> THE SEARCH DEBOUNCE OWNS A TIMER; IT MUST BE CLEARED ON UNMOUNT. <<<
// v1 never clears its debounce ref on unmount. Leaving the surface within 300ms
// of a keystroke then fires `setQuery` on an unmounted page. Harmless in React
// 18 (the no-op setState warning was removed) but still a stray timer, and
// Wave 2 already shipped a fix for a debounce writing stale search state.
//
// >>> EVERY CLEAR PATH GOES THROUGH `clearSearch`. <<<
// That timer also has to be cancelled by the box's own "×" and by "Clear all".
// v1 cancels in neither, so typing "ma" and clearing within the 300ms window
// re-applies "ma" AFTER the box is empty — a filtered grid and an active-filter
// chip with nothing on screen to explain either. One helper owns cancel + box +
// committed value, so a third clear path cannot forget one of the three. The
// raw `setSearchValue` is deliberately NOT returned: exposing it is what let a
// caller empty the box without cancelling anything in the first place.
//
// >>> A SEARCH THE BOX DENIES MUST NOT KEEP FILTERING (D6 acceptance). <<<
// v1 bails out below the 2-char minimum without touching `query.search`
// (CatalogBrowsePage.tsx:432), so "mazak" backspaced to "m" leaves the GRID
// filtered by "mazak" and `catalogActiveFilterCount` counting a search the user
// cannot see; only deleting to fully empty resets it. Carried v1 quirks are
// normally preserved verbatim, but this one is a filter silently applied that
// the UI denies is applied, and it defeats D6's own acceptance criterion
// ("activeFilterCount becomes accurate"). Below the minimum the committed value
// is therefore CLEARED. The v1 file is left untouched — its twin is booked
// separately.
//
// >>> WHY THIS HOOK FETCHES MACHINE TYPES (D6). <<<
// The control-system-brand and operation-type option lists are reference tables
// keyed BY MACHINE TYPE (`GET /v1/customer/machines/types/{machineTypeId}/...`,
// CustomerMachinesController.cs:260 and :280). Catalog models are all CNC — the
// catalog add path forces `typeId` to the CNC type (MachineDialog.tsx:338-339)
// — so the CNC type id is the right key, and it has to be resolved from
// `getTypes()` first. See CatalogFilterRail.tsx's header for why these two
// endpoints are the correct source for CATALOG filter ids.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { CatalogBrowseQuery, CatalogModelListItem, MachineTypes } from "../../models/models";
import {
  browseCatalogModels,
  getCatalogCategories,
  getCatalogManufacturers,
  getCatalogTaperTypes,
} from "../../machineCatalogSlice";
import {
  getControlSystemBrands,
  getOperationTypes,
  getTypes,
} from "../../customerMachinerySlice";
import { CATALOG_DEFAULT_QUERY } from "./catalogQuery";

/** §11: model search is 2-char minimum, 300ms debounce, server-side. */
const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 300;

export const useCatalogBrowse = () => {
  const dispatch = useAppDispatch();

  const manufacturers = useAppSelector((s) => s.machineCatalog.manufacturers);
  const categories = useAppSelector((s) => s.machineCatalog.categories);
  const taperTypes = useAppSelector((s) => s.machineCatalog.taperTypes);
  const types = useAppSelector((s) => s.customerMachinery.types);
  const operationTypes = useAppSelector((s) => s.customerMachinery.operationTypes);
  const controlSystemBrands = useAppSelector((s) => s.customerMachinery.controlSystemBrands);

  const [query, setQuery] = useState<CatalogBrowseQuery>(CATALOG_DEFAULT_QUERY);
  const [searchValue, setSearchValue] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [browseResults, setBrowseResults] = useState<CatalogModelListItem[]>([]);
  const [browseTotalCount, setBrowseTotalCount] = useState(0);
  // Starts true: the first load is dispatched from the effect below, so the
  // first paint must be the skeleton, never a one-frame empty state.
  const [browseLoading, setBrowseLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Stamp of the newest dispatched browse — see the header block. */
  const requestRef = useRef(0);

  const load = useCallback(
    (next: CatalogBrowseQuery) => {
      requestRef.current += 1;
      const seq = requestRef.current;
      setBrowseLoading(true);
      setFailed(false);
      dispatch(browseCatalogModels(next))
        .unwrap()
        .then((result) => {
          if (seq !== requestRef.current) return;
          setBrowseResults(result.items);
          setBrowseTotalCount(result.totalCount);
          setBrowseLoading(false);
        })
        .catch(() => {
          if (seq !== requestRef.current) return;
          setFailed(true);
          setBrowseLoading(false);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(getCatalogCategories());
    dispatch(getCatalogTaperTypes());
    if (types.length === 0) dispatch(getTypes());
    // Mount-once bootstrap; `types.length` is READ, not depended on — depending
    // on it would re-run the whole block the moment the fetch lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  /** D6 option lists, once the CNC type id is known. */
  useEffect(() => {
    const cnc = types.find((t) => t.name === MachineTypes.cnc);
    if (!cnc) return;
    dispatch(getOperationTypes(cnc.id));
    dispatch(getControlSystemBrands(cnc.id));
  }, [types, dispatch]);

  /** The facet list is per category — v1's :273-275. */
  useEffect(() => {
    dispatch(getCatalogManufacturers({ categoryCode: query.categoryCode }));
  }, [dispatch, query.categoryCode]);

  useEffect(() => {
    load(query);
  }, [load, query]);

  const cancelSearchDebounce = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
  }, []);

  useEffect(() => () => cancelSearchDebounce(), [cancelSearchDebounce]);

  /** Drops a COMMITTED search and touches nothing else. Returns the very same
   *  query object when there is nothing committed, so typing the first
   *  character of a fresh search cannot refire the load effect. */
  const dropCommittedSearch = useCallback(() => {
    setQuery((prev) =>
      prev.search === undefined ? prev : { ...prev, search: undefined, page: 1 }
    );
  }, []);

  /** The ONE clear path: the box's "×" and "Clear all" both come through here,
   *  so neither can leave a pending debounce armed. */
  const clearSearch = useCallback(() => {
    cancelSearchDebounce();
    setSearchValue("");
    dropCommittedSearch();
  }, [cancelSearchDebounce, dropCommittedSearch]);

  /** v1's handleSearchInputChange (:423-439), with the timer cleanup above and
   *  the below-minimum clear the header block explains. */
  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      cancelSearchDebounce();
      const trimmed = value.trim();
      if (trimmed.length < SEARCH_MIN_CHARS) {
        dropCommittedSearch();
        return;
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        setQuery((prev) => ({ ...prev, search: trimmed, page: 1 }));
      }, SEARCH_DEBOUNCE_MS);
    },
    [cancelSearchDebounce, dropCommittedSearch]
  );

  /** Enter commits immediately — v1's handleSearchKeyDown :440-454. */
  const onSearchSubmit = useCallback(() => {
    cancelSearchDebounce();
    const trimmed = searchValue.trim();
    setQuery((prev) => ({
      ...prev,
      search: trimmed.length >= SEARCH_MIN_CHARS ? trimmed : undefined,
      page: 1,
    }));
  }, [cancelSearchDebounce, searchValue]);

  const retry = useCallback(() => load(query), [load, query]);

  return {
    query,
    setQuery,
    searchValue,
    onSearchChange,
    onSearchSubmit,
    clearSearch,
    brandSearch,
    setBrandSearch,
    browseResults,
    browseTotalCount,
    browseLoading,
    failed,
    retry,
    manufacturers,
    categories,
    taperTypes,
    operationTypes,
    controlSystemBrands,
  };
};

export default useCatalogBrowse;
